import React, { useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IFormData, ITask } from "@/api/tasks/types";
import { CircularLoader } from "@/components/Layout/CircularLoader";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { OptionsSprint } from "./OptionsSprint";
import { IGroupedSection } from "../DetailProject";
import { EmptyItem } from "../EmptyItem";
import { useReusableSensors } from "../../../utils/sensors";
import { useDragOverStyling } from "@/features/projects/hooks/useDragOverStyling";
import detailProjectStyles from '@features/projects/styles/detailproject.module.scss';
import SortableTaskListRow from "@/features/projects/components/projectsPage/SortableTaskListRow";
import TaskListRow from "@/features/projects/components/projectsPage/TaskListRow";
import { useAssignedUsers } from "@/features/projects/hooks/useAssignedUsers";
import { useGetAllCommentsByTask } from "@/features/projects/hooks/comments/useGetAllCommentsByTask";
import { useGetAllDocsByTask } from "@/features/projects/hooks/tasks/useGetAllDocsByTask";

export interface IDetailProjectListProps {
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (result: DragEndEvent) => void;
  dragMoveFn: (event: DragMoveEvent) => void;
  tasksData: ITask[];
  selectTask: (task: ITask) => void;
  newTask: (form: IFormData) => void;
  groupedSections: IGroupedSection[] | [];
  activeTask: ITask | null;
}

// Componente para el indicador de inserción
const DropIndicator = () => (
  <tr className={detailProjectStyles["task-table__drop-indicator"]}>
    <td colSpan={6}>
      <div className={detailProjectStyles["task-table__drop-line"]}></div>
    </td>
  </tr>
);

const TaskOverlay = ({ task }: { task: ITask }) => {
  const { userAssigned } = useAssignedUsers({ taskId: task.id });
  const { comments } = useGetAllCommentsByTask(task.id);
  const { docs } = useGetAllDocsByTask(task.id);
  let status: 'completado' | 'en progreso' | 'pendiente' = 'pendiente';
  if (task.state === 'completada') status = 'completado';
  else if (task.state === 'en progreso') status = 'en progreso';

  return (
    <table style={{ width: '100%', backgroundColor: 'var(--color-surface-raised)', boxShadow: '0 5px 15px rgba(0,0,0,0.2)' }}>
      <tbody>
        <tr className={detailProjectStyles["task-table__row"]}>
          <TaskListRow
            index={0}
            title={task.title}
            assignees={userAssigned.map(u => ({
              name: `${u.firstname} ${u.firstlastname}`.trim(),
              avatarUrl: u.picture || undefined
            }))}
            dateRange={task.startDate && task.endDate ? { start: task.startDate, end: task.endDate } : undefined}
            status={status}
            commentsCount={Array.isArray(comments) ? comments.length : 0}
            attachmentsCount={Array.isArray(docs) ? docs.length : 0}
            styles={detailProjectStyles}
          />
        </tr>
      </tbody>
    </table>
  );
};

export const DetailProjectList = ({
  onDragEnd,
  onDragStart,
  dragMoveFn,
  selectTask,
  newTask,
  groupedSections,
  activeTask
}: IDetailProjectListProps) => {
  
  console.log('DetailProjectList - groupedSections:', groupedSections);
  const [activeId, setActiveId] = useState({ id: 0, active: false });
  const [collapsedSections, setCollapsedSections] = useState<Record<number, boolean>>({});
  const [dropIndicator, setDropIndicator] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);
  const { draggingTaskPositions } = useSelector((state: RootState) => state.projects);
  const { activeDroppable, handleDragOver: originalHandleDragOver, resetActiveDroppable } = useDragOverStyling();

  const handleClick = (id: number) => {
    setActiveId({ id: id, active: true });
  };

  const handleCancel = (id: number, state: boolean) => {
    setActiveId({ id: id, active: state });
  };

  const handleSectionCollapse = (sectionId: number) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleDragOverWithIndicator = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over || !active) {
      setDropIndicator(null);
      return;
    }

    // Si es un área vacía
    if (over.id.toString().startsWith('emptyRow_')) {
      const sectionId = over.id.toString().split('_')[1];
      setDropIndicator({ sectionId, index: 0 });
      return;
    }

    // Encontrar la tarea y sección sobre la que se está arrastrando
    const overId = over.id.toString();
    let overSectionId: string | undefined;
    let overTaskIndex = -1;

    for (const section of groupedSections) {
      const taskIndex = section.tasks.findIndex(task => task.id === overId);
      if (taskIndex !== -1) {
        overSectionId = section.id.toString();
        overTaskIndex = taskIndex;
        break;
      }
    }

    if (overSectionId !== undefined && overTaskIndex !== -1) {
      // Determinar si insertar antes o después basado en la posición del mouse
      // Por simplicidad, insertamos después de la tarea sobre la que se arrastra
      setDropIndicator({ 
        sectionId: overSectionId, 
        index: overTaskIndex + 1 
      });
    }
    
    // Llamar al handler original para mantener la funcionalidad existente
    originalHandleDragOver(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    onDragEnd(event);
    setDropIndicator(null);
    resetActiveDroppable();
  };

  return (
    <DndContext
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={dragMoveFn}
      onDragOver={handleDragOverWithIndicator}
      sensors={useReusableSensors()}
      collisionDetection={closestCenter}
    >
      <div className={`task-list-container ${detailProjectStyles["task-list-container"] || ''}`}>
        {groupedSections?.length > 0
          ? groupedSections?.map((section: IGroupedSection) => {
            const isActive = activeDroppable === section.id.toString();
            const isCollapsed = collapsedSections[section.id];
            const isDraggingOver = draggingTaskPositions[section.id.toString()];
            const taskIds = section.tasks?.map(task => task.id) || [];

            return (
              <div
                className={`task-section ${isActive ? "task-section--active" : ''}
                  ${isDraggingOver ? "task-section--dragging" : ''}
                  ${isCollapsed ? "task-section--collapsed" : ''}`}
                key={section.id}
              >
                <div className="task-section__title">
                  <OptionsSprint
                    sectionData={section}
                    key={section?.id}
                    newTask={newTask}
                    isCollapsed={isCollapsed}
                    activeId={activeId}
                    handleClick={handleClick}
                    handleCancel={handleCancel}
                    handlerShowSection={handleSectionCollapse}
                  />
                </div>
                {!isCollapsed && (
                  <div>
                    {section?.tasks?.length > 0 ? (
                      <table className={detailProjectStyles["task-table"]}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Nombre</th>
                            <th>Responsable</th>
                            <th>Fecha</th>
                            <th>Estado</th>
                            <th></th>
                          </tr>
                        </thead>
                        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                          <tbody>
                            {section.tasks.map((task, idx) => (
                              <React.Fragment key={`task-group-${task.id}`}>
                                {dropIndicator?.sectionId === section.id.toString() && 
                                 dropIndicator.index === idx && (
                                  <DropIndicator key={`drop-${idx}`} />
                                )}
                                <SortableTaskListRow key={task.id} task={task} idx={idx} selectTask={selectTask} />
                              </React.Fragment>
                            ))}
                            {dropIndicator?.sectionId === section.id.toString() && 
                             dropIndicator.index === section.tasks.length && (
                              <DropIndicator key={`drop-end`} />
                            )}
                          </tbody>
                        </SortableContext>
                      </table>
                    ) : (
                      <>
                        {dropIndicator?.sectionId === section.id.toString() && (
                          <div className={detailProjectStyles["task-table__drop-indicator-empty"]}>
                            <div className={detailProjectStyles["task-table__drop-line"]}></div>
                          </div>
                        )}
                        <EmptyItem itemId={`emptyRow_${section.id}`} />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
          : <CircularLoader />
        }
      </div>
      {activeTask && (
        <DragOverlay>
          <TaskOverlay task={activeTask} />
        </DragOverlay>
      )}
    </DndContext>
  );
};
